import * as React from 'react'
import { useMemo, useState } from 'react'

import { Button, Typography } from '~/components'
import { DatePickerPopover } from '~/components/common/DatePickerPopover'
import { Selector } from '~/components/common/Selector'
import { Modal, ModalBody, ModalFooter } from '~/components/modals/CurieModal'
import { UsedCommandType } from '~/constants/commands'
import { useRepositoryBranches } from '~/hooks/useCodeContext'
import {
  AgentRequest,
  CodeContext,
  CommandType,
  ConnectionType,
  ExecutionMode,
  IntegrationPlatform,
  Metadata,
  VerificationMode
} from '~/protos/agents'
import { CreateScheduledTaskPayload, useCreateScheduledTask } from '~/services/scheduledTaskService'
import { extractFileDisplayName } from '~/utils/repositoryUtils'
import { BetterToast } from '~/utils/toast'

const SCHEDULABLE_REPO_CONN_TYPES = new Set([
  'github',
  'github_custom',
  'gitlab',
  'gitlab_custom',
  'bitbucket',
  'azure_devops'
])

type ScheduledTaskType = 'GENERATE_SIMPLE_CODE_PR' | 'INTEGRATION_TEST'

const TASK_TYPE_OPTIONS: Array<{
  key: ScheduledTaskType
  value: ScheduledTaskType
  label: string
}> = [
  { key: 'GENERATE_SIMPLE_CODE_PR', value: 'GENERATE_SIMPLE_CODE_PR', label: 'Code Task' },
  { key: 'INTEGRATION_TEST', value: 'INTEGRATION_TEST', label: 'Integration Test' }
]

type RepeatPattern = 'once' | 'daily' | 'weekly' | 'monthly' | 'weekdays' | 'custom'

type CustomIntervalUnit = 'minutes' | 'hours' | 'days' | 'weeks' | 'months'

type EndsType = 'never' | 'on' | 'after'

interface ScheduleConfig {
  repeatPattern: RepeatPattern
  intervalEvery: number
  customUnit: CustomIntervalUnit
  runDate: string
  timeOfDay: string
  dayOfWeek: string
  dayOfMonth: string
  /** When repeatPattern is 'custom' with weeks unit, list of selected day-of-week keys */
  weekDays: string[]
  endsType: EndsType
  endsOnDate: string
  endsAfterCount: number
}

interface ScheduledTaskRepository {
  conn_type: string
  index_name: string
}

interface SelectedScheduledTaskRepository {
  conn_type: string
  repository: string
}

function splitRepositoryPath(repository: string): { orgName: string; repoName: string } {
  const slashIndex = repository.indexOf('/')
  if (slashIndex === -1) {
    return { orgName: '', repoName: repository }
  }
  return {
    orgName: repository.slice(0, slashIndex),
    repoName: repository.slice(slashIndex + 1) || repository
  }
}

const REPEAT_OPTIONS: Array<{
  key: RepeatPattern
  value: RepeatPattern
  label: string
}> = [
  { key: 'once', value: 'once', label: 'Does not repeat' },
  { key: 'daily', value: 'daily', label: 'Daily' },
  { key: 'weekly', value: 'weekly', label: 'Weekly' },
  { key: 'monthly', value: 'monthly', label: 'Monthly' },
  { key: 'weekdays', value: 'weekdays', label: 'Weekdays (Mon–Fri)' },
  { key: 'custom', value: 'custom', label: 'Custom…' }
]

const CUSTOM_UNIT_OPTIONS: Array<{
  key: CustomIntervalUnit
  value: CustomIntervalUnit
  label: string
}> = [
  { key: 'minutes', value: 'minutes', label: 'minutes' },
  { key: 'hours', value: 'hours', label: 'hours' },
  { key: 'days', value: 'days', label: 'days' },
  { key: 'weeks', value: 'weeks', label: 'weeks' },
  { key: 'months', value: 'months', label: 'months' }
]

const DAY_OF_WEEK_OPTIONS = [
  { key: 'MON', value: 'MON', label: 'Monday' },
  { key: 'TUE', value: 'TUE', label: 'Tuesday' },
  { key: 'WED', value: 'WED', label: 'Wednesday' },
  { key: 'THU', value: 'THU', label: 'Thursday' },
  { key: 'FRI', value: 'FRI', label: 'Friday' },
  { key: 'SAT', value: 'SAT', label: 'Saturday' },
  { key: 'SUN', value: 'SUN', label: 'Sunday' }
]

const DAY_OF_MONTH_OPTIONS = Array.from({ length: 28 }, (_, index) => {
  const day = String(index + 1)
  return { key: day, value: day, label: day }
})

function toLocalDateString(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function getDefaultRunDate(): string {
  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)
  return toLocalDateString(tomorrow)
}

function getDefaultScheduleConfig(): ScheduleConfig {
  return {
    repeatPattern: 'daily',
    intervalEvery: 1,
    customUnit: 'days',
    runDate: getDefaultRunDate(),
    timeOfDay: '09:00',
    dayOfWeek: 'MON',
    dayOfMonth: '1',
    weekDays: ['MON'],
    endsType: 'never',
    endsOnDate: getDefaultRunDate(),
    endsAfterCount: 13
  }
}

function isSchedulableRepository(repo: ScheduledTaskRepository): boolean {
  return SCHEDULABLE_REPO_CONN_TYPES.has(repo.conn_type.toLowerCase())
}

function buildRepositorySelectorItems(repos: ScheduledTaskRepository[]) {
  return repos.map(repo => {
    const value = JSON.stringify({
      conn_type: repo.conn_type,
      repository: repo.index_name
    })
    return {
      key: value,
      value,
      label: extractFileDisplayName(repo.index_name)
    }
  })
}

function parseRepositorySelection(repoKey: string): SelectedScheduledTaskRepository | undefined {
  if (!repoKey) return undefined
  try {
    return JSON.parse(repoKey) as SelectedScheduledTaskRepository
  } catch {
    return undefined
  }
}

export function CreateScheduledTaskModal({
  isOpen,
  onClose
}: {
  isOpen: boolean
  onClose: () => void
}) {
  const createSchedule = useCreateScheduledTask()
  const [taskType, setTaskType] = useState<ScheduledTaskType>('GENERATE_SIMPLE_CODE_PR')
  const { repos, getBranches } = useRepositoryBranches(
    (taskType === 'INTEGRATION_TEST'
      ? CommandType.INTEGRATION_TEST
      : CommandType.GENERATE_SIMPLE_CODE_PR) as UsedCommandType
  )
  const [title, setTitle] = useState('')
  const [prompt, setPrompt] = useState('')
  const [flowNames, setFlowNames] = useState('')
  const [deploymentUrl, setDeploymentUrl] = useState('')
  const [repoKey, setRepoKey] = useState('')
  const [branch, setBranch] = useState('')
  const [scheduleConfig, setScheduleConfig] = useState(getDefaultScheduleConfig)
  const [minimumScheduleDate, setMinimumScheduleDate] = useState(getDefaultRunDate)
  const [isCustomRecurrenceOpen, setCustomRecurrenceOpen] = useState(false)

  const resetForm = React.useCallback(() => {
    const nextScheduleConfig = getDefaultScheduleConfig()
    setTaskType('GENERATE_SIMPLE_CODE_PR')
    setTitle('')
    setPrompt('')
    setFlowNames('')
    setDeploymentUrl('')
    setRepoKey('')
    setBranch('')
    setScheduleConfig(nextScheduleConfig)
    setMinimumScheduleDate(nextScheduleConfig.runDate)
    setCustomRecurrenceOpen(false)
  }, [])

  const wasOpenRef = React.useRef(false)
  React.useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      resetForm()
    }
    wasOpenRef.current = isOpen
  }, [isOpen, resetForm])

  const selectedRepo = useMemo(() => {
    return parseRepositorySelection(repoKey)
  }, [repoKey])

  const branches = useMemo(
    () => (selectedRepo ? getBranches(selectedRepo.conn_type, selectedRepo.repository) : []),
    [getBranches, selectedRepo]
  )
  const effectiveBranch = branch || branches[0] || ''
  const isIntegrationTest = taskType === 'INTEGRATION_TEST'
  const selectableRepos = useMemo(
    () => (isIntegrationTest ? repos : repos.filter(isSchedulableRepository)),
    [isIntegrationTest, repos]
  )
  const isCreateDisabled =
    !title.trim() ||
    !prompt.trim() ||
    !selectedRepo ||
    !effectiveBranch ||
    (scheduleConfig.repeatPattern === 'once' && !scheduleConfig.runDate) ||
    (scheduleConfig.endsType === 'on' && !scheduleConfig.endsOnDate) ||
    !hasRequiredScheduleTimes(scheduleConfig) ||
    (isIntegrationTest && (!flowNames.trim() || !deploymentUrl.trim())) ||
    createSchedule.isPending

  const projectItems = useMemo(
    () => buildRepositorySelectorItems(selectableRepos),
    [selectableRepos]
  )

  const branchItems = useMemo(
    () => branches.map(branchName => ({ key: branchName, value: branchName, label: branchName })),
    [branches]
  )

  const handleRepoChange = (nextRepoKey: string) => {
    setRepoKey(nextRepoKey)
    setBranch('')
  }

  const handleTaskTypeChange = (nextTaskType: string) => {
    setTaskType(nextTaskType as ScheduledTaskType)
    setRepoKey('')
    setBranch('')
  }

  const updateScheduleConfig = (updates: Partial<typeof scheduleConfig>) => {
    setScheduleConfig(current => ({ ...current, ...updates }))
  }

  const handleClose = () => {
    if (createSchedule.isPending) return
    resetForm()
    onClose()
  }

  const handleCreate = async () => {
    if (!selectedRepo || !effectiveBranch) return
    try {
      await createSchedule.mutateAsync(
        buildCreateSchedulePayload({
          taskType,
          title: title.trim(),
          prompt: prompt.trim(),
          flowNames: flowNames.trim(),
          deploymentUrl: deploymentUrl.trim(),
          repository: selectedRepo.repository,
          connType: selectedRepo.conn_type,
          branch: effectiveBranch,
          scheduleConfig
        })
      )
      BetterToast.success('Scheduled task created')
      resetForm()
      onClose()
    } catch (ex) {
      BetterToast.error(ex instanceof Error ? ex.message : 'Failed to create scheduled task')
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} className='max-w-[760px] w-full'>
      <ModalBody className='flex flex-col gap-5'>
        <div className='flex items-start justify-between gap-4'>
          <div className='flex flex-col gap-1'>
            <Typography
              fontFamily='geist'
              fontSize='titleMd'
              fontWeight='semibold'
              className='select-none'
            >
              New scheduled task
            </Typography>
            <Typography
              fontFamily='geist'
              fontSize='bodyMd'
              className='select-none text-tokens-neutral-text-secondary'
            >
              Create a recurring task by storing the task type, repository, prompt, and schedule.
            </Typography>
          </div>
        </div>

        <div className='grid gap-4 sm:grid-cols-[220px_minmax(0,1fr)]'>
          <label className='flex flex-col gap-2'>
            <Typography
              fontFamily='geist'
              fontSize='labelLg'
              fontWeight='semibold'
              className='select-none'
            >
              Task type
            </Typography>
            <Selector
              options={TASK_TYPE_OPTIONS}
              value={taskType}
              onSelectionChange={handleTaskTypeChange}
              placeholder='Select task type'
              className='w-full'
              triggerClassName='h-10 px-3 py-0'
              data-testid='scheduled-task-type-select'
            />
          </label>

          <label className='flex flex-col gap-2'>
            <Typography
              fontFamily='geist'
              fontSize='labelLg'
              fontWeight='semibold'
              className='select-none'
            >
              Name
            </Typography>
            <input
              value={title}
              onChange={e => setTitle(e.currentTarget.value)}
              placeholder='e.g. Weekly security sweep'
              className='h-10 rounded-[12px] border border-tokens-neutral-border-1 px-3 text-sm text-tokens-neutral-text-primary outline-none focus:border-tokens-status-link-1'
              data-testid='scheduled-task-title-input'
            />
          </label>
        </div>

        <label className='flex flex-col gap-2'>
          <Typography
            fontFamily='geist'
            fontSize='labelLg'
            fontWeight='semibold'
            className='select-none'
          >
            Prompt
          </Typography>
          <textarea
            value={prompt}
            onChange={e => setPrompt(e.currentTarget.value)}
            rows={7}
            placeholder='Example: Check for outdated connector versions and open a PR with safe upgrades and tests.'
            data-testid='scheduled-task-prompt-input'
            className='resize-none rounded-[12px] border border-tokens-neutral-border-1 px-3 py-2 text-sm text-tokens-neutral-text-primary outline-none focus:border-tokens-status-link-1'
          />
          <Typography
            fontFamily='geist'
            fontSize='bodySm'
            className='select-none text-tokens-neutral-text-secondary'
          >
            Each scheduled run uses this exact prompt with the selected task type, repository, and
            branch.
          </Typography>
        </label>

        <div className='grid gap-4 sm:grid-cols-2'>
          <label className='flex flex-col gap-2'>
            <Typography
              fontFamily='geist'
              fontSize='labelLg'
              fontWeight='semibold'
              className='select-none'
            >
              Repository
            </Typography>
            <Selector
              options={projectItems}
              value={repoKey}
              onSelectionChange={handleRepoChange}
              placeholder='Select repository'
              disabled={selectableRepos.length === 0}
              className='w-full'
              triggerClassName='h-10 px-3 py-0'
              data-testid='scheduled-task-repo-select'
            />
          </label>

          {selectedRepo ? (
            <label className='flex flex-col gap-2'>
              <Typography
                fontFamily='geist'
                fontSize='labelLg'
                fontWeight='semibold'
                className='select-none'
              >
                Branch
              </Typography>
              <Selector
                options={branchItems}
                value={effectiveBranch}
                onSelectionChange={setBranch}
                placeholder='Select branch'
                disabled={branchItems.length === 0}
                className='w-full'
                triggerClassName='h-10 px-3 py-0'
                data-testid='scheduled-task-branch-select'
              />
            </label>
          ) : null}

          {isIntegrationTest ? (
            <>
              <label className='flex flex-col gap-2'>
                <Typography
                  fontFamily='geist'
                  fontSize='labelLg'
                  fontWeight='semibold'
                  className='select-none'
                >
                  Flow names
                </Typography>
                <input
                  value={flowNames}
                  onChange={e => setFlowNames(e.currentTarget.value)}
                  placeholder='e.g. get-members-flow, create-order-flow'
                  className='h-10 rounded-[12px] border border-tokens-neutral-border-1 px-3 text-sm text-tokens-neutral-text-primary outline-none focus:border-tokens-status-link-1'
                  data-testid='scheduled-task-flow-names-input'
                />
              </label>

              <label className='flex flex-col gap-2'>
                <Typography
                  fontFamily='geist'
                  fontSize='labelLg'
                  fontWeight='semibold'
                  className='select-none'
                >
                  Deployment URL
                </Typography>
                <input
                  value={deploymentUrl}
                  onChange={e => setDeploymentUrl(e.currentTarget.value)}
                  placeholder='https://app.example.com'
                  className='h-10 rounded-[12px] border border-tokens-neutral-border-1 px-3 text-sm text-tokens-neutral-text-primary outline-none focus:border-tokens-status-link-1'
                  data-testid='scheduled-task-deployment-url-input'
                />
              </label>
            </>
          ) : null}

          <div className='flex flex-col gap-2 sm:col-span-2'>
            <Typography
              fontFamily='geist'
              fontSize='labelLg'
              fontWeight='semibold'
              className='select-none'
            >
              Repeats
            </Typography>
            <ScheduleRecurrenceRow
              scheduleConfig={scheduleConfig}
              minimumScheduleDate={minimumScheduleDate}
              onChange={updateScheduleConfig}
              onRequestCustom={() => setCustomRecurrenceOpen(true)}
            />
          </div>
        </div>

        {selectableRepos.length === 0 ? (
          <Typography
            fontFamily='geist'
            fontSize='bodySm'
            className='select-none text-tokens-neutral-text-secondary'
          >
            {isIntegrationTest
              ? 'Connect a repository to schedule integration tests.'
              : 'Connect GitHub, GitLab, Bitbucket, or Azure DevOps to attach scheduled tasks to a repository.'}
          </Typography>
        ) : null}
      </ModalBody>
      <ModalFooter>
        <Button variant='borderless' onClick={handleClose}>
          Cancel
        </Button>
        <Button
          onClick={handleCreate}
          isDisabled={isCreateDisabled}
          isLoading={createSchedule.isPending}
        >
          Create
        </Button>
      </ModalFooter>
      <CustomRecurrenceModal
        isOpen={isCustomRecurrenceOpen}
        onClose={() => setCustomRecurrenceOpen(false)}
        initialConfig={scheduleConfig}
        minimumScheduleDate={minimumScheduleDate}
        onSave={updates => updateScheduleConfig(updates)}
      />
    </Modal>
  )
}

function buildCreateSchedulePayload({
  taskType,
  title,
  prompt,
  flowNames,
  deploymentUrl,
  repository,
  connType,
  branch,
  scheduleConfig
}: {
  taskType: ScheduledTaskType
  title: string
  prompt: string
  flowNames: string
  deploymentUrl: string
  repository: string
  connType: string
  branch: string
  scheduleConfig: ScheduleConfig
}): CreateScheduledTaskPayload {
  const { orgName, repoName } = splitRepositoryPath(repository)
  const commandType =
    taskType === 'INTEGRATION_TEST'
      ? CommandType.INTEGRATION_TEST
      : CommandType.GENERATE_SIMPLE_CODE_PR
  const commandString = taskType === 'INTEGRATION_TEST' ? flowNames : prompt
  const userNotes =
    taskType === 'INTEGRATION_TEST'
      ? `Deployment URL: ${deploymentUrl}\nAdditional notes: ${prompt}`
      : ''
  const request = AgentRequest.fromPartial({
    connectionType: ConnectionType.CURIE_UI,
    command: {
      commandType,
      commandString,
      executionMode: ExecutionMode.AGENT,
      verificationMode: VerificationMode.VERIFY
    },
    metadata: Metadata.fromPartial({
      codeContext: CodeContext.fromPartial({
        connType,
        orgName,
        repoName: repoName || repository,
        branchName: branch
      })
    }),
    platform: IntegrationPlatform.MULESOFT,
    userNotes
  })

  return {
    name: title,
    description: prompt,
    cmd_type: taskType,
    // This is the AgentRequest source connection type. The selected repository
    // provider is stored separately in metadata.codeContext.connType.
    conn_type: 'CURIE_UI',
    agent_request_json: AgentRequest.toJSON(request) as Record<string, unknown>,
    schedule: buildScheduleSpec(scheduleConfig)
  }
}

function ScheduleRecurrenceRow({
  scheduleConfig,
  minimumScheduleDate,
  onChange,
  onRequestCustom
}: {
  scheduleConfig: ScheduleConfig
  minimumScheduleDate: string
  onChange: (updates: Partial<ScheduleConfig>) => void
  onRequestCustom: () => void
}) {
  const isCustom = scheduleConfig.repeatPattern === 'custom'
  const showsTime =
    !isCustom ||
    scheduleConfig.customUnit === 'months' ||
    (scheduleConfig.customUnit === 'weeks' && Math.max(1, scheduleConfig.intervalEvery) === 1)

  const handleRepeatChange = (value: string) => {
    if (value === 'custom') {
      onChange({ repeatPattern: 'custom' })
      onRequestCustom()
      return
    }
    onChange({ repeatPattern: value as RepeatPattern })
  }

  return (
    <div className='flex flex-wrap items-center gap-3'>
      <div className='flex items-center gap-2'>
        <Selector
          options={REPEAT_OPTIONS}
          value={scheduleConfig.repeatPattern}
          onSelectionChange={handleRepeatChange}
          placeholder='Select schedule'
          triggerClassName='h-10 px-3 py-0'
          className='w-[220px]'
          data-testid='scheduled-task-repeat-select'
        />

        {isCustom ? (
          <Button
            variant='outlined'
            onClick={onRequestCustom}
            className='h-10 px-3 text-sm'
            data-testid='scheduled-task-custom-edit'
          >
            Edit
          </Button>
        ) : null}
      </div>

      <div className='flex flex-wrap items-center gap-2'>
        {isCustom ? (
          <Typography
            fontFamily='geist'
            fontSize='bodyMd'
            className='select-none text-tokens-neutral-text-secondary'
          >
            {summarizeCustomRecurrence(scheduleConfig)}
          </Typography>
        ) : null}

        {scheduleConfig.repeatPattern === 'weekly' ? (
          <span className='inline-flex items-center gap-2'>
            <Typography
              fontFamily='geist'
              fontSize='bodyMd'
              className='select-none text-tokens-neutral-text-secondary'
            >
              on
            </Typography>
            <Selector
              options={DAY_OF_WEEK_OPTIONS}
              value={scheduleConfig.dayOfWeek}
              onSelectionChange={value => onChange({ dayOfWeek: value })}
              placeholder='Select day'
              triggerClassName='h-10 px-3 py-0'
              className='w-[160px]'
              data-testid='scheduled-task-day-select'
            />
          </span>
        ) : null}

        {scheduleConfig.repeatPattern === 'monthly' ? (
          <span className='inline-flex items-center gap-2'>
            <Typography
              fontFamily='geist'
              fontSize='bodyMd'
              className='select-none text-tokens-neutral-text-secondary'
            >
              on day
            </Typography>
            <Selector
              options={DAY_OF_MONTH_OPTIONS}
              value={scheduleConfig.dayOfMonth}
              onSelectionChange={value => onChange({ dayOfMonth: value })}
              placeholder='Select day'
              triggerClassName='h-10 px-3 py-0'
              className='w-[100px]'
              data-testid='scheduled-task-month-day-select'
            />
          </span>
        ) : null}

        {scheduleConfig.repeatPattern === 'once' ? (
          <span className='inline-flex items-center gap-2'>
            <Typography
              fontFamily='geist'
              fontSize='bodyMd'
              className='select-none text-tokens-neutral-text-secondary'
            >
              on
            </Typography>
            <DatePickerPopover
              value={scheduleConfig.runDate}
              onChange={value => onChange({ runDate: value })}
              min={minimumScheduleDate}
              placeholder='Pick a date'
              className='w-[180px]'
              aria-label='Run date'
              data-testid='scheduled-task-date-input'
            />
          </span>
        ) : null}

        {showsTime ? (
          <span className='inline-flex items-center gap-2'>
            <Typography
              fontFamily='geist'
              fontSize='bodyMd'
              className='select-none text-tokens-neutral-text-secondary'
            >
              at
            </Typography>
            <input
              type='time'
              value={scheduleConfig.timeOfDay}
              onChange={event => onChange({ timeOfDay: event.currentTarget.value })}
              className='h-10 w-[140px] rounded-[12px] border border-tokens-neutral-border-1 bg-white px-3 text-sm text-tokens-neutral-text-primary outline-none transition-colors focus:border-tokens-status-link-1'
              aria-label='Select time'
              data-testid='scheduled-task-time-select'
            />
          </span>
        ) : null}
      </div>
    </div>
  )
}

function summarizeCustomRecurrence(config: ScheduleConfig): string {
  const everyN = Math.max(1, config.intervalEvery)
  const unit = config.customUnit
  const unitLabel = everyN === 1 ? unit.replace(/s$/, '') : unit
  if (unit === 'weeks') {
    if (everyN > 1) {
      return `every ${everyN} ${unitLabel}`
    }
    const days = sortWeekDays(config.weekDays.length > 0 ? config.weekDays : [config.dayOfWeek])
    const dayLabels = days
      .map(key => DAY_OF_WEEK_OPTIONS.find(option => option.key === key)?.label.slice(0, 3) ?? key)
      .join(', ')
    return `every ${everyN} ${unitLabel} on ${dayLabels}`
  }
  if (unit === 'months') {
    const monthLabel = everyN === 1 ? unitLabel : `calendar ${unitLabel}`
    return `every ${everyN} ${monthLabel} on day ${config.dayOfMonth}`
  }
  return `every ${everyN} ${unitLabel}`
}

const WEEKDAY_CHIP_ORDER = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'] as const

function sortWeekDays(days: string[]): string[] {
  return WEEKDAY_CHIP_ORDER.filter(day => days.includes(day))
}

function CustomRecurrenceModal({
  isOpen,
  onClose,
  initialConfig,
  minimumScheduleDate,
  onSave
}: {
  isOpen: boolean
  onClose: () => void
  initialConfig: ScheduleConfig
  minimumScheduleDate: string
  onSave: (updates: Partial<ScheduleConfig>) => void
}) {
  const [intervalEvery, setIntervalEvery] = useState(initialConfig.intervalEvery)
  const [customUnit, setCustomUnit] = useState<CustomIntervalUnit>(initialConfig.customUnit)
  const [weekDays, setWeekDays] = useState<string[]>(
    initialConfig.weekDays.length > 0 ? initialConfig.weekDays : [initialConfig.dayOfWeek]
  )
  const [dayOfMonth, setDayOfMonth] = useState(initialConfig.dayOfMonth)
  const [endsType, setEndsType] = useState<EndsType>(initialConfig.endsType)
  const [endsOnDate, setEndsOnDate] = useState(initialConfig.endsOnDate)
  const [endsAfterCount, setEndsAfterCount] = useState(initialConfig.endsAfterCount)

  const hasSyncedOpenStateRef = React.useRef(false)
  React.useEffect(() => {
    if (!isOpen) {
      hasSyncedOpenStateRef.current = false
      return
    }
    if (hasSyncedOpenStateRef.current) return
    hasSyncedOpenStateRef.current = true
    setIntervalEvery(initialConfig.intervalEvery)
    setCustomUnit(initialConfig.customUnit)
    setWeekDays(
      initialConfig.weekDays.length > 0 ? initialConfig.weekDays : [initialConfig.dayOfWeek]
    )
    setDayOfMonth(initialConfig.dayOfMonth)
    setEndsType(initialConfig.endsType)
    setEndsOnDate(initialConfig.endsOnDate)
    setEndsAfterCount(initialConfig.endsAfterCount)
  }, [
    isOpen,
    initialConfig.intervalEvery,
    initialConfig.customUnit,
    initialConfig.weekDays,
    initialConfig.dayOfWeek,
    initialConfig.dayOfMonth,
    initialConfig.endsType,
    initialConfig.endsOnDate,
    initialConfig.endsAfterCount
  ])

  const handleSave = () => {
    onSave({
      intervalEvery: Math.max(1, intervalEvery),
      customUnit,
      weekDays,
      dayOfMonth,
      // Keep the legacy single-day fields in sync for non-custom paths.
      dayOfWeek: weekDays[0] ?? initialConfig.dayOfWeek,
      endsType,
      endsOnDate,
      endsAfterCount: Math.max(1, endsAfterCount)
    })
    onClose()
  }

  const toggleDay = (key: string) => {
    setWeekDays(current => {
      if (current.includes(key) && current.length === 1) {
        return current
      }
      const next = current.includes(key)
        ? current.filter(value => value !== key)
        : [...current, key]
      return sortWeekDays(next)
    })
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} className='max-w-[460px] w-full'>
      <ModalBody className='flex flex-col gap-6 px-7 py-6'>
        <Typography
          fontFamily='geist'
          fontSize='titleLg'
          fontWeight='semibold'
          className='select-none'
        >
          Custom recurrence
        </Typography>

        <div className='flex items-center gap-3'>
          <Typography
            fontFamily='geist'
            fontSize='bodyMd'
            className='select-none text-tokens-neutral-text-primary w-[110px]'
          >
            Repeat every
          </Typography>
          <input
            type='number'
            min={1}
            value={intervalEvery}
            onChange={event =>
              setIntervalEvery(Math.max(1, Number(event.currentTarget.value) || 1))
            }
            className='h-10 w-[80px] rounded-[8px] bg-tokens-neutral-fill-fill-6 px-3 text-sm text-tokens-neutral-text-primary outline-none focus:ring-2 focus:ring-tokens-status-link-1'
            data-testid='custom-recurrence-interval-input'
          />
          <Selector
            options={CUSTOM_UNIT_OPTIONS}
            value={customUnit}
            onSelectionChange={value => setCustomUnit(value as CustomIntervalUnit)}
            placeholder='Select unit'
            triggerClassName='h-10 px-3 py-0'
            className='w-[140px]'
            data-testid='custom-recurrence-unit-select'
          />
        </div>

        {customUnit === 'weeks' && Math.max(1, intervalEvery) === 1 ? (
          <div className='flex flex-col gap-3'>
            <Typography
              fontFamily='geist'
              fontSize='bodyMd'
              className='select-none text-tokens-neutral-text-primary'
            >
              Repeat on
            </Typography>
            <div className='flex items-center gap-2' role='group' aria-label='Repeat on weekdays'>
              {WEEKDAY_CHIP_ORDER.map(key => {
                const day = DAY_OF_WEEK_OPTIONS.find(option => option.key === key)
                if (!day) return null
                const isSelected = weekDays.includes(day.key)
                return (
                  <button
                    key={day.key}
                    type='button'
                    onClick={() => toggleDay(day.key)}
                    aria-pressed={isSelected}
                    aria-label={day.label}
                    title={day.label}
                    className={
                      'h-9 w-9 rounded-full text-sm font-medium select-none transition-colors ' +
                      (isSelected
                        ? 'bg-tokens-status-link-1 text-white'
                        : 'bg-tokens-neutral-fill-fill-6 text-tokens-neutral-text-tertiary hover:bg-tokens-neutral-fill-fill-4')
                    }
                    data-testid={`custom-recurrence-day-${day.key}`}
                  >
                    {day.label.charAt(0)}
                  </button>
                )
              })}
            </div>
          </div>
        ) : null}

        {customUnit === 'weeks' && Math.max(1, intervalEvery) > 1 ? (
          <Typography
            fontFamily='geist'
            fontSize='bodySm'
            className='select-none text-tokens-neutral-text-secondary'
          >
            Weekday selection is only available for schedules that repeat every 1 week.
          </Typography>
        ) : null}

        {customUnit === 'months' ? (
          <div className='flex flex-col gap-2'>
            <div className='flex items-center gap-3'>
              <Typography
                fontFamily='geist'
                fontSize='bodyMd'
                className='select-none text-tokens-neutral-text-primary w-[110px]'
              >
                On day
              </Typography>
              <Selector
                options={DAY_OF_MONTH_OPTIONS}
                value={dayOfMonth}
                onSelectionChange={value => setDayOfMonth(value)}
                placeholder='Select day'
                triggerClassName='h-10 px-3 py-0'
                className='w-[100px]'
                data-testid='custom-recurrence-month-day-select'
              />
            </div>
            {Math.max(1, intervalEvery) > 1 ? (
              <Typography
                fontFamily='geist'
                fontSize='bodySm'
                className='select-none text-tokens-neutral-text-secondary'
              >
                Multi-month schedules are calendar-aligned, not relative to the creation date.
              </Typography>
            ) : null}
          </div>
        ) : null}

        <div className='flex flex-col gap-3'>
          <Typography
            fontFamily='geist'
            fontSize='bodyMd'
            className='select-none text-tokens-neutral-text-primary'
          >
            Ends
          </Typography>

          <label className='flex items-center gap-3'>
            <input
              type='radio'
              name='custom-recurrence-ends'
              checked={endsType === 'never'}
              onChange={() => setEndsType('never')}
              className='h-4 w-4 accent-tokens-status-link-1'
              data-testid='custom-recurrence-ends-never'
            />
            <Typography fontFamily='geist' fontSize='bodyMd' className='select-none'>
              Never
            </Typography>
          </label>

          <label className='flex items-center gap-3'>
            <input
              type='radio'
              name='custom-recurrence-ends'
              checked={endsType === 'on'}
              onChange={() => setEndsType('on')}
              className='h-4 w-4 accent-tokens-status-link-1'
              data-testid='custom-recurrence-ends-on'
            />
            <Typography fontFamily='geist' fontSize='bodyMd' className='select-none w-[60px]'>
              On
            </Typography>
            <DatePickerPopover
              value={endsOnDate}
              onChange={setEndsOnDate}
              min={minimumScheduleDate}
              isDisabled={endsType !== 'on'}
              placeholder='Pick an end date'
              className='h-9 w-[180px]'
              aria-label='Recurrence end date'
              data-testid='custom-recurrence-ends-on-date'
            />
          </label>

          <label className='flex items-center gap-3'>
            <input
              type='radio'
              name='custom-recurrence-ends'
              checked={endsType === 'after'}
              onChange={() => setEndsType('after')}
              className='h-4 w-4 accent-tokens-status-link-1'
              data-testid='custom-recurrence-ends-after'
            />
            <Typography fontFamily='geist' fontSize='bodyMd' className='select-none w-[60px]'>
              After
            </Typography>
            <input
              type='number'
              min={1}
              value={endsAfterCount}
              disabled={endsType !== 'after'}
              onChange={event =>
                setEndsAfterCount(Math.max(1, Number(event.currentTarget.value) || 1))
              }
              className={
                'h-9 w-[80px] rounded-[8px] bg-tokens-neutral-fill-fill-6 px-3 text-sm outline-none focus:ring-2 focus:ring-tokens-status-link-1 ' +
                (endsType === 'after'
                  ? 'text-tokens-neutral-text-primary'
                  : 'text-tokens-neutral-text-disabled cursor-not-allowed')
              }
              data-testid='custom-recurrence-ends-after-count'
            />
            <Typography
              fontFamily='geist'
              fontSize='bodyMd'
              className={
                'select-none ' +
                (endsType === 'after'
                  ? 'text-tokens-neutral-text-primary'
                  : 'text-tokens-neutral-text-disabled')
              }
            >
              occurrences
            </Typography>
          </label>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant='borderless' onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleSave} data-testid='custom-recurrence-save'>
          Done
        </Button>
      </ModalFooter>
    </Modal>
  )
}

function parseTimeOfDay(timeOfDay: string): { hour: number; minute: number } {
  const match = /^(\d{1,2}):(\d{2})$/.exec(timeOfDay)
  if (!match) return { hour: 9, minute: 0 }
  const hour = Number(match[1])
  const minute = Number(match[2])
  if (!Number.isInteger(hour) || !Number.isInteger(minute)) {
    return { hour: 9, minute: 0 }
  }
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) {
    return { hour: 9, minute: 0 }
  }
  return { hour, minute }
}

function toLocalDateTimeIso(
  dateYmd: string,
  hour: number,
  minute: number,
  second = 0,
  millisecond = 0
): string | undefined {
  if (!dateYmd) return undefined
  const [year, month, day] = dateYmd.split('-').map(Number)
  const localDate = new Date(year, month - 1, day, hour, minute, second, millisecond)
  if (Number.isNaN(localDate.getTime())) return undefined
  if (
    localDate.getFullYear() !== year ||
    localDate.getMonth() !== month - 1 ||
    localDate.getDate() !== day
  ) {
    return undefined
  }
  const offsetMinutes = localDate.getTimezoneOffset()
  const offsetSign = offsetMinutes <= 0 ? '+' : '-'
  const absOffsetMinutes = Math.abs(offsetMinutes)
  const offsetHours = String(Math.floor(absOffsetMinutes / 60)).padStart(2, '0')
  const offsetMins = String(absOffsetMinutes % 60).padStart(2, '0')
  const localMonth = String(localDate.getMonth() + 1).padStart(2, '0')
  const localDay = String(localDate.getDate()).padStart(2, '0')
  const localHour = String(localDate.getHours()).padStart(2, '0')
  const localMinute = String(localDate.getMinutes()).padStart(2, '0')
  const localSecond = String(localDate.getSeconds()).padStart(2, '0')
  const localMillisecond = String(localDate.getMilliseconds()).padStart(3, '0')

  return [
    `${localDate.getFullYear()}-${localMonth}-${localDay}`,
    `T${localHour}:${localMinute}:${localSecond}.${localMillisecond}`,
    `${offsetSign}${offsetHours}:${offsetMins}`
  ].join('')
}

function hasRequiredScheduleTimes(config: ScheduleConfig): boolean {
  const { hour, minute } = parseTimeOfDay(config.timeOfDay)
  if (config.repeatPattern === 'once') {
    return Boolean(toLocalDateTimeIso(config.runDate, hour, minute))
  }
  if (config.endsType === 'on') {
    return Boolean(toLocalDateTimeIso(config.endsOnDate, 23, 59, 59, 999))
  }
  return true
}

function applyEndsToSpec(
  spec: CreateScheduledTaskPayload['schedule'],
  config: ScheduleConfig
): CreateScheduledTaskPayload['schedule'] {
  if (config.endsType === 'on') {
    // The date picker captures a local calendar day; store its UTC instant
    // boundary explicitly instead of relying on string-date parsing.
    const endAt = toLocalDateTimeIso(config.endsOnDate, 23, 59, 59, 999)
    return endAt ? { ...spec, end_at: endAt } : spec
  }
  if (config.endsType === 'after') {
    return { ...spec, max_runs: Math.max(1, config.endsAfterCount) }
  }
  return spec
}

function buildScheduleSpec(config: ScheduleConfig): CreateScheduledTaskPayload['schedule'] {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  const { hour, minute } = parseTimeOfDay(config.timeOfDay)

  if (config.repeatPattern === 'once') {
    const runAt = toLocalDateTimeIso(config.runDate, hour, minute)
    if (!runAt) {
      throw new Error('Select a valid run date before creating this schedule')
    }
    return {
      schedule_kind: 'ONCE',
      run_at: runAt,
      timezone
    }
  }

  if (config.repeatPattern === 'custom') {
    const everyN = Math.max(1, config.intervalEvery)
    if (config.customUnit === 'minutes') {
      return applyEndsToSpec(
        {
          schedule_kind: 'INTERVAL',
          interval_seconds: Math.max(60, everyN * 60),
          timezone
        },
        config
      )
    }
    if (config.customUnit === 'hours') {
      return applyEndsToSpec(
        {
          schedule_kind: 'INTERVAL',
          interval_seconds: everyN * 3600,
          timezone
        },
        config
      )
    }
    if (config.customUnit === 'days') {
      return applyEndsToSpec(
        {
          schedule_kind: 'INTERVAL',
          interval_seconds: everyN * 24 * 3600,
          timezone
        },
        config
      )
    }
    if (config.customUnit === 'weeks') {
      const days = sortWeekDays(
        config.weekDays.length > 0 ? config.weekDays : [config.dayOfWeek]
      ).join(',')
      // For "every 1 week", use a clean weekly cron; for >1 weeks, fall back to
      // an interval since cron lacks native "every N weeks" semantics.
      if (everyN === 1) {
        return applyEndsToSpec(
          {
            schedule_kind: 'CRON',
            cron_expression: `${minute} ${hour} * * ${days}`,
            timezone
          },
          config
        )
      }
      return applyEndsToSpec(
        {
          schedule_kind: 'INTERVAL',
          interval_seconds: everyN * 7 * 24 * 3600,
          timezone
        },
        config
      )
    }
    return applyEndsToSpec(
      {
        schedule_kind: 'CRON',
        cron_expression: `${minute} ${hour} ${config.dayOfMonth} */${everyN} *`,
        timezone
      },
      config
    )
  }

  const cronExpressionByPattern: Partial<Record<RepeatPattern, string>> = {
    daily: `${minute} ${hour} * * *`,
    weekdays: `${minute} ${hour} * * MON-FRI`,
    weekly: `${minute} ${hour} * * ${config.dayOfWeek}`,
    monthly: `${minute} ${hour} ${config.dayOfMonth} * *`
  }
  return applyEndsToSpec(
    {
      schedule_kind: 'CRON',
      cron_expression: cronExpressionByPattern[config.repeatPattern] ?? `${minute} ${hour} * * *`,
      timezone
    },
    config
  )
}
